/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import moment from "moment";
import DataTable from "react-data-table-component";
import Input from "../../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FiTrash } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { MdOutlineRemoveRedEye } from "react-icons/md";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import { deleteLeadById, getLeadList } from "../../../service/api";

/* Over-the-counter items: `otcItems` is the current name+quantity shape;
   `otc` was an earlier free-text field, kept so older leads still render. */
const formatOtcItems = (details) => {
  const items = (details?.otcItems || [])
    .filter((item) => item?.name)
    .map((item) => (item.quantity ? `${item.name} (x${item.quantity})` : item.name));
  return items.length ? items.join(", ") : details?.otc || "";
};

/* Patient address (transfer form) as a single line */
const formatAddress = (details) =>
  [details?.address, details?.city, details?.state, details?.zip]
    .filter(Boolean)
    .join(", ");

/* Human readable summary for the table's details column */
const leadSummary = (formType, row) => {
  switch (formType) {
    case "contact":
      return row?.message;
    case "refill":
      return (row?.details?.rxNumbers || []).filter(Boolean).join(", ");
    case "transfer":
      return row?.details?.transferAll
        ? "Transfer all prescriptions"
        : (row?.details?.medications || [])
            .filter((m) => m?.name)
            .map((m) => (m.rx ? `${m.name} (${m.rx})` : m.name))
            .join(", ");
    case "auto-refill":
      return row?.details?.meds;
    default:
      return "";
  }
};

/* Label/value rows rendered inside the view dialog */
const leadDetailRows = (formType, row) => {
  const common = [
    { label: "Name", value: row?.name },
    { label: "Email", value: row?.email },
    { label: "Phone Number", value: row?.phoneNumber },
  ];

  const yesNo = (value) => (value ? "Yes" : "No");

  switch (formType) {
    case "contact":
      return [
        ...common,
        { label: "Subject", value: row?.subject },
        { label: "ZIP Code", value: row?.zip },
        { label: "Message", value: row?.message },
      ];
    case "refill":
      return [
        ...common,
        { label: "Date of Birth", value: row?.dob },
        {
          label: "Rx Numbers",
          value: (row?.details?.rxNumbers || []).filter(Boolean).join(", "),
        },
        { label: "OTC Items", value: formatOtcItems(row?.details) },
        { label: "Pickup or Delivery", value: row?.details?.method },
        { label: "Notify When Ready", value: row?.details?.notify },
      ];
    case "transfer":
      return [
        ...common,
        { label: "Date of Birth", value: row?.dob },
        { label: "Address", value: formatAddress(row?.details) },
        { label: "Current Pharmacy", value: row?.details?.pharmacy },
        { label: "Current Pharmacy Phone", value: row?.details?.pharmacyPhone },
        {
          label: "Transfer All Prescriptions",
          value: yesNo(row?.details?.transferAll),
        },
        {
          label: "Medications",
          value: (row?.details?.medications || [])
            .filter((m) => m?.name || m?.rx)
            .map((m) => (m.rx ? `${m.name || "—"} (${m.rx})` : m.name))
            .join(", "),
        },
      ];
    case "auto-refill":
      return [
        ...common,
        { label: "Date of Birth", value: row?.dob },
        { label: "Prescriptions to Enroll", value: row?.details?.meds },
        { label: "Notify When Ready", value: row?.details?.notify },
        { label: "Auto Refill Consent", value: yesNo(row?.details?.consent) },
      ];
    default:
      return common;
  }
};

const Leads = ({ formType, title, description }) => {
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [leadsList, setLeadsList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState("");

  const [selectedLead, setSelectedLead] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* FUNCTIONS */
  const handlePageChange = (e) => {
    setPage(e);
  };

  const handlePerPageChange = (e) => {
    setSizePerPage(e);
  };

  const getLeadsListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
      formType: formType,
    };
    if (search) {
      params.search = search;
    }

    getLeadList(params)
      .then((res) => {
        setLeadsList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteLeadById(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        getLeadsListFunction();
        setLoading(false);
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.3,
      selector: (row, index) => (
        <span className="ml-2 text-sm font-ibm-semibold text-black text-opacity-60">
          {(page - 1) * sizePerPage + index + 1}
        </span>
      ),
    },
    {
      name: "Name",
      selector: (row) => (
        <p
          className="text-sm font-ibm-semibold text-black"
          style={{ overflowWrap: "anywhere" }}
        >
          {row?.name}
        </p>
      ),
    },
    {
      name: "Email & Phone",
      grow: 1.4,
      selector: (row) => (
        <>
          {row?.email && (
            <a
              href={`mailto:${row.email}`}
              className="block text-sm font-ibm-regular text-blue-500"
            >
              {row.email}
            </a>
          )}
          {row?.phoneNumber && (
            <a
              href={`tel:${row.phoneNumber}`}
              className="mt-1 block text-sm font-ibm-regular text-blue-500"
            >
              {row.phoneNumber}
            </a>
          )}
        </>
      ),
    },
    {
      name: "Details",
      grow: 2,
      selector: (row) => (
        <span
          className="text-sm font-ibm-regular text-greyText leading-relaxed"
          style={{ overflowWrap: "anywhere" }}
        >
          {leadSummary(formType, row)}
        </span>
      ),
    },
    {
      name: "Date & Time",
      selector: (row) => (
        <span className="text-sm font-ibm-regular text-greyText">
          {moment(row?.createdAt).format("MMM DD, YYYY hh:mm a")}
        </span>
      ),
    },
    {
      name: "Actions",
      grow: 0.5,
      selector: (row) => (
        <div className="flex items-center gap-1">
          <Tooltip content="View" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setSelectedLead(row);
                setViewModal(true);
              }}
              className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <MdOutlineRemoveRedEye className="w-4 h-4 text-greyText" />
            </Button>
          </Tooltip>
          <Tooltip content="Delete" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setDeleteId(row?._id);
                setDeleteModal(true);
              }}
              className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <FiTrash className="w-4 h-4 text-greyText" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getLeadsListFunction();
  }, [page, sizePerPage, search, formType]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no requests to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5">
      {/* PAGE TITLE */}
      <Helmet>
        <title>{title} | WellCare Pharmacy</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              {title}
            </h1>
            <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
              {description}
            </h2>
          </div>
          <div className="block xl:flex lg:flex items-center gap-2">
            {/* SEARCHBAR */}
            <div className="w-[40vh]">
              <Input
                type="text"
                placeholder="Search..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e?.target?.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? leadsList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>

        {/* DELETE LEAD */}
        <Dialog
          open={deleteModal}
          handler={() => setDeleteModal(!deleteModal)}
          size="xs"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Delete
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setDeleteModal(!deleteModal)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <p className="text-base font-ibm-regular text-black">
                Are you sure you want to delete the details of this request
                from the database?
              </p>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                onClick={handleDelete}
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                {loading ? "Please wait.." : "Confirm, delete"}
              </Button>
            </div>
          </DialogBody>
        </Dialog>

        {/* VIEW LEAD */}
        <Dialog
          open={viewModal}
          handler={() => setViewModal(!viewModal)}
          size="sm"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Request Details
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setViewModal(!viewModal)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder max-h-[60vh] overflow-y-auto">
              {selectedLead && (
                <div>
                  {leadDetailRows(formType, selectedLead)
                    .filter((row) => row.value)
                    .map((row) => (
                      <p
                        key={row.label}
                        className="text-base font-ibm-semibold text-black mb-4"
                        style={{ overflowWrap: "anywhere" }}
                      >
                        <strong className="font-ibm-regular">
                          {row.label}:
                        </strong>{" "}
                        {row.value}
                      </p>
                    ))}
                  {selectedLead?.attachments?.length > 0 && (
                    <div className="mb-4">
                      <strong className="text-base font-ibm-regular text-black">
                        Attachments:
                      </strong>
                      <div className="mt-2 flex flex-col gap-2">
                        {selectedLead.attachments.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-ibm-regular text-blue-500 hover:underline break-all"
                          >
                            {`Attachment ${i + 1}`} — {url.split("/").pop()}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-base font-ibm-semibold text-black">
                    <strong className="font-ibm-regular">Timestamp:</strong>{" "}
                    {moment(selectedLead?.createdAt).format(
                      "MMM DD, YYYY hh:mm a"
                    )}
                  </p>
                </div>
              )}
            </div>
          </DialogBody>
        </Dialog>
      </div>
    </section>
  );
};

export default Leads;
